/**
 * AANKH - Header Component
 * 
 * @author Harshit Khemani
 * @version 1.4.0
 * @description Modern, clean header with streamlined design
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevice, ConnectionStatus } from '../hooks/useDevice';
import wordmarkSvg from '../../assets/AANKH/AANKH-wordmark.svg';

/**
 * Battery Indicator Component
 */
function BatteryIndicator({ level }) {
  const fillWidth = Math.max(0, Math.min(100, level || 0));
  const isLow = level < 20;
  const isMedium = level >= 20 && level < 50;
  
  return (
    <div className="flex items-center gap-1.5">
      <div className="relative w-6 h-3.5">
        {/* Battery outline */}
        <div className="absolute inset-0 rounded-[2px] border border-gray-300/70 bg-gray-50/50" />
        
        {/* Battery fill */}
        <motion.div 
          className={`absolute left-0.5 top-0.5 bottom-0.5 rounded-[1px]
            ${isLow 
              ? 'bg-gradient-to-r from-red-500 to-red-600' 
              : isMedium 
                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                : 'bg-gradient-to-r from-green-500 to-green-600'}`}
          initial={{ width: 0 }}
          animate={{ width: `${fillWidth * 0.87}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
        
        {/* Battery tip */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0.5 h-2 bg-gray-300/70 rounded-r-[1px]" />
      </div>
      <span 
        className={`text-[10px] font-semibold tabular-nums
          ${isLow ? 'text-red-600' : isMedium ? 'text-yellow-600' : 'text-green-600'}`}
      >
        {level}%
      </span>
    </div>
  );
}

/**
 * Header Component
 */
function Header({ onOpenSettings }) {
  const { 
    connectionStatus, 
    isConnected, 
    deviceInfo, 
    disconnect,
  } = useDevice();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo */}
          <motion.div 
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.a
              href="/"
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <img
                src={wordmarkSvg}
                alt="AANKH"
                className="h-7 sm:h-8"
              />
            </motion.a>
          </motion.div>

          {/* Right: Connection Status & Actions */}
          <div className="flex items-center gap-3">
            <AnimatePresence mode="wait">
              {isConnected ? (
                <motion.div
                  key="connected"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-3"
                >
                  {/* Device Info - Desktop */}
                  <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-xl bg-gray-50/80 border border-gray-200/60">
                    <div className="relative">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-500/50 animate-ping" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {deviceInfo.name}
                    </span>
                    {deviceInfo.batteryLevel !== null && (
                      <>
                        <div className="w-px h-4 bg-gray-300/60" />
                        <BatteryIndicator level={deviceInfo.batteryLevel} />
                      </>
                    )}
                  </div>

                  {/* Device Info - Mobile */}
                  <div className="md:hidden flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50/80 border border-gray-200/60">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs font-medium text-gray-900 truncate max-w-[100px]">
                      {deviceInfo.name}
                    </span>
                  </div>

                  {/* Disconnect Button */}
                  <motion.button
                    onClick={disconnect}
                    className="px-4 py-2 rounded-xl text-sm font-medium
                      bg-gray-100 text-gray-700 hover:bg-gray-200
                      active:scale-95 transition-colors min-h-[36px]"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="hidden sm:inline">Disconnect</span>
                    <span className="sm:hidden">DC</span>
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="disconnected"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 text-sm text-gray-500"
                >
                  {connectionStatus === ConnectionStatus.CONNECTING ? (
                    <>
                      <motion.div
                        className="w-2 h-2 rounded-full bg-primary"
                        animate={{ 
                          scale: [1, 1.3, 1],
                          opacity: [1, 0.5, 1]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <span className="hidden sm:inline font-medium">Connecting...</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                      <span className="hidden sm:inline font-medium">Not connected</span>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Settings Button */}
            <motion.button
              onClick={onOpenSettings}
              className="p-2 rounded-xl text-gray-600 hover:bg-gray-100
                active:scale-95 transition-colors min-w-[36px] min-h-[36px]
                flex items-center justify-center"
              whileHover={{ scale: 1.05, rotate: 90 }}
              whileTap={{ scale: 0.95 }}
              title="Settings"
              aria-label="Settings"
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" 
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
                />
                <path strokeLinecap="round" strokeLinejoin="round" 
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                />
              </svg>
            </motion.button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;